/**
 * 时间范围（CST 自然日）的唯一一份实现：报表页与中控看板共用同一枚控件、同一套窗口算式。
 *
 * 依据 docs/reports/design.md 第 2.8 节（六枚快捷窗口、含首含尾）与 coord/reports-req/adjudication-01.md。
 * 两页不得各写一份；本文件是六枚窗口算式的唯一真源。
 * 与本仓既有 period（today/week/month）不混用：既有 week 是近 7 日滚动窗、month 是近 30 日滚动窗，
 * 本批六枚是自然日窗；两套口径并存是已裁决结论，本文件只负责新的 from/to 一侧。
 */

export type QuickRangeKey = 'today' | 'yesterday' | 'last7' | 'last15' | 'thisMonth' | 'lastMonth';

export interface DateRange {
  from: string;
  to: string;
}

export interface QuickRangeOption {
  key: QuickRangeKey;
  label: string;
}

/** 六枚快捷的顺序与文案：今日／昨日／近7天／近15天／本月／上月（控件里不再出现「本周」）。 */
export const QUICK_RANGE_OPTIONS: readonly QuickRangeOption[] = [
  { key: 'today', label: '今日' },
  { key: 'yesterday', label: '昨日' },
  { key: 'last7', label: '近7天' },
  { key: 'last15', label: '近15天' },
  { key: 'thisMonth', label: '本月' },
  { key: 'lastMonth', label: '上月' },
];

/** 默认态：报表页＝近15天（需求默认区间）；中控看板＝今日（对齐既有缺省兜底）。 */
export const REPORT_DEFAULT_QUICK: QuickRangeKey = 'last15';
export const DASHBOARD_DEFAULT_QUICK: QuickRangeKey = 'today';

export const DATE_TEXT_PATTERN = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
export const RANGE_MISSING_TEXT = '起止日期需同时填写';
export const RANGE_FORMAT_TEXT = '日期格式需为 YYYY-MM-DD';
export const RANGE_ORDER_TEXT = '起始日期不能晚于结束日期';

const CST_OFFSET_MS = 8 * 60 * 60 * 1000;

interface DateParts {
  y: number;
  m: number;
  d: number;
}

function pad2(value: number): string {
  return value < 10 ? '0' + value : String(value);
}

function toText(y: number, m: number, d: number): string {
  return y + '-' + pad2(m) + '-' + pad2(d);
}

/** 严格解析：格式不符或日历上不存在的日期（如 02-30）一律返回 null。 */
export function parseDateText(value: unknown): DateParts | null {
  if (typeof value !== 'string' || !DATE_TEXT_PATTERN.test(value)) return null;
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return { y, m, d };
}

export function isDateText(value: unknown): value is string {
  return parseDateText(value) !== null;
}

/** CST（UTC+8）壁钟当日；不得拿 UTC 的 Date 当今日。 */
export function cstToday(now: Date = new Date()): string {
  const shifted = new Date(now.getTime() + CST_OFFSET_MS);
  return toText(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

export function addDays(day: string, delta: number): string {
  const parts = parseDateText(day);
  if (!parts) return day;
  const shifted = new Date(Date.UTC(parts.y, parts.m - 1, parts.d + delta));
  return toText(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

export function monthStart(day: string): string {
  const parts = parseDateText(day);
  return parts ? toText(parts.y, parts.m, 1) : day;
}

/** 按自然月位移，入参一律取当月 1 日再位移，避免月末溢出把 1 月 31 日减成 12 月 1 日之外的怪值。 */
export function shiftMonths(day: string, delta: number): string {
  const parts = parseDateText(day);
  if (!parts) return day;
  const shifted = new Date(Date.UTC(parts.y, parts.m - 1 + delta, 1));
  return toText(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** 六枚快捷的窗口算式（唯一真源，含首含尾）。 */
export function quickRange(key: QuickRangeKey, today: string = cstToday()): DateRange {
  const base = isDateText(today) ? today : cstToday();
  switch (key) {
    case 'today':
      return { from: base, to: base };
    case 'yesterday': {
      const day = addDays(base, -1);
      return { from: day, to: day };
    }
    case 'last7':
      return { from: addDays(base, -6), to: base };
    case 'last15':
      return { from: addDays(base, -14), to: base };
    case 'thisMonth':
      return { from: monthStart(base), to: base };
    case 'lastMonth': {
      const first = shiftMonths(monthStart(base), -1);
      return { from: first, to: addDays(shiftMonths(first, 1), -1) };
    }
    default:
      return { from: base, to: base };
  }
}

/** 反查当前区间命中的快捷；手选或残缺区间返回 null（用于清空高亮）。 */
export function activeQuickKey(
  range: Partial<DateRange> | null | undefined,
  today: string = cstToday(),
): QuickRangeKey | null {
  if (!range || !range.from || !range.to) return null;
  for (const option of QUICK_RANGE_OPTIONS) {
    const window = quickRange(option.key, today);
    if (window.from === range.from && window.to === range.to) return option.key;
  }
  return null;
}

export interface RangeValidation {
  ok: boolean;
  reason?: 'missing' | 'format' | 'order';
  message: string;
}

/** 前端三态校验：缺一端／格式不符／起始晚于结束；残缺区间一律不上送。 */
export function validateRange(range: Partial<DateRange> | null | undefined): RangeValidation {
  const from = range?.from ?? '';
  const to = range?.to ?? '';
  if (!from || !to) return { ok: false, reason: 'missing', message: RANGE_MISSING_TEXT };
  if (!isDateText(from) || !isDateText(to)) return { ok: false, reason: 'format', message: RANGE_FORMAT_TEXT };
  if (from > to) return { ok: false, reason: 'order', message: RANGE_ORDER_TEXT };
  return { ok: true, message: '' };
}

/** 展示文案：同年省写为 2026-10-01 ~ 10-15，跨年写全。 */
export function rangeText(range: Partial<DateRange> | null | undefined): string {
  const from = range?.from ?? '';
  const to = range?.to ?? '';
  if (!isDateText(from) || !isDateText(to)) return '';
  return from.slice(0, 4) === to.slice(0, 4) ? from + ' ~ ' + to.slice(5) : from + ' ~ ' + to;
}
